import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ApplicationInsightsService, SeverityLevel } from '../services/application-insights.service';
import { environment } from '../../../environments/environment';
import { UrlNormalizer } from '../utils/url-normalizer';

@Injectable()
export class ApplicationInsightsInterceptor implements HttpInterceptor {
  private readonly appInsightsService = inject(ApplicationInsightsService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only track requests to the application's own API
    // Skip external APIs (like HERE Maps, Mixpanel, etc.) and static assets
    const isApplicationApi = request.url.startsWith(environment.apiUrl) ||
                             request.url.startsWith('/api/') ||
                             request.url.startsWith('api/');

    if (!isApplicationApi) {
      return next.handle(request);
    }

    // Skip OPTIONS requests (CORS preflight)
    if (request.method === 'OPTIONS') {
      return next.handle(request);
    }

    const startTime = Date.now();
    const correlationId = request.headers.get('x-correlation-id');
    const normalizedUrl = this.normalizeUrl(request.url);

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const duration = Date.now() - startTime;

          this.appInsightsService.trackEvent('HTTP Request', {
            url: normalizedUrl,
            method: request.method,
            statusCode: event.status,
            duration: duration,
            correlationId: correlationId,
            success: true
          });

          this.appInsightsService.trackMetric(`HTTP ${request.method} Duration`, duration, {
            url: normalizedUrl,
            statusCode: event.status
          });
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const duration = Date.now() - startTime;

        this.appInsightsService.trackEvent('HTTP Request Failed', {
          url: normalizedUrl,
          method: request.method,
          statusCode: error.status,
          duration: duration,
          correlationId: correlationId,
          errorMessage: error.message,
          success: false
        });

        // Track exception for 5xx server errors
        if (error.status >= 500) {
          this.appInsightsService.trackException(
            new Error(`HTTP ${error.status}: ${error.message}`),
            SeverityLevel.Error,
            {
              url: normalizedUrl,
              method: request.method,
              statusCode: error.status,
              correlationId: correlationId
            }
          );
        }

        throw error;
      })
    );
  }

  /**
   * Normalize URL by replacing ID parameters with placeholders for better aggregation
   * Examples:
   *   /api/Shipments/116 -> /api/Shipments/{id}
   *   /api/Users/a1b2c3d4-e5f6-7890 -> /api/Users/{guid}
   *   /api/Orders/123/Items/456 -> /api/Orders/{id}/Items/{id}
   */
  private normalizeUrl(url: string): string {
    return UrlNormalizer.normalize(url);
  }
}
